import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND_VIDEO, BRAND_VIDEO_POSTER, videoObjectJsonLd } from "@/lib/site";
import { track } from "@/lib/analytics";

// Brand/ad video with VideoObject structured data for video SEO.
// autoPlay=true starts it muted on mount (browser-compliant) with a tap-to-unmute control.
// showEndCta overlays a "create your free account" prompt after it finishes (Quiz / Calculator funnels).
export default function AdVideo({ className = "", schema = true, showEndCta = false, autoPlay = false, ctaHeading = "Ready to get started, free?" }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!autoPlay) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play?.();
    if (p && typeof p.then === "function") p.then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [autoPlay]);

  const start = () => {
    setEnded(false);
    setPlaying(true);
    setMuted(false);
    track("video_play", { video: "sc_ad" });
    const v = videoRef.current;
    if (v) { v.muted = false; v.play?.(); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    const next = !muted;
    setMuted(next);
    if (v) { v.muted = next; if (!next) v.play?.(); }
  };

  return (
    <div className={`relative w-full rounded-2xl p-[1.5px] bg-gradient-to-br from-violet-500/50 via-white/10 to-emerald-500/40 shadow-[0_25px_70px_-20px_rgba(109,40,217,0.55)] ${className}`} data-testid="brand-video">
      {schema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(videoObjectJsonLd) }} />}
      <div className="relative w-full aspect-video rounded-[15px] overflow-hidden bg-slate-950">
        <video ref={videoRef} src={`${BRAND_VIDEO}#t=0.1`} poster={BRAND_VIDEO_POSTER} controls={playing && !ended && !muted}
          muted={muted} autoPlay={autoPlay} playsInline preload={autoPlay ? "auto" : "metadata"}
          className="w-full h-full object-cover"
          onPlay={() => setPlaying(true)}
          onEnded={() => { setEnded(true); track("video_complete", { video: "sc_ad" }); }} />

        {!playing && (
          <button type="button" aria-label="Tap for sound to play the video" data-testid="brand-video-play" onClick={start}
            className="absolute inset-0 flex items-center justify-center bg-slate-950/10 hover:bg-slate-950/5 transition-colors group">
            <span className="inline-flex items-center gap-2.5 rounded-full bg-violet-600/90 group-hover:bg-violet-500 group-hover:scale-105 px-8 py-4 text-base sm:text-lg font-bold text-white shadow-2xl ring-4 ring-white/10 transition-all duration-200">
              <Volume2 className="w-6 h-6" /> Tap for sound
            </span>
          </button>
        )}

        {playing && !ended && muted && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <button type="button" onClick={toggleMute} data-testid="brand-video-unmute"
              className="pointer-events-auto inline-flex items-center gap-2.5 rounded-full bg-slate-950/75 hover:bg-slate-900 backdrop-blur px-7 py-4 text-base sm:text-lg font-bold text-white border border-white/25 shadow-2xl hover:scale-105 transition-all duration-200">
              <VolumeX className="w-6 h-6" /> Tap for sound
            </button>
          </div>
        )}
        {playing && !ended && !muted && (
          <button type="button" onClick={toggleMute} aria-label="Mute" data-testid="brand-video-mute"
            className="absolute bottom-3 right-3 inline-flex items-center justify-center rounded-full bg-slate-950/60 hover:bg-slate-900 backdrop-blur w-9 h-9 text-white border border-white/20 transition-colors">
            <Volume2 className="w-4 h-4" />
          </button>
        )}

        {ended && showEndCta && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/90 backdrop-blur-sm p-6 text-center" data-testid="video-end-cta">
            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white">{ctaHeading}</h3>
            <p className="text-sm text-slate-300 mt-2 max-w-sm">Join free, get discovered as a closer, or find your next great hire.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-5 w-full max-w-md">
              <Link to="/signup/salesman" className="flex-1" onClick={() => track("video_cta_click", { role: "salesman" })}>
                <Button data-testid="video-cta-salesman" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold">I'm a Closer</Button>
              </Link>
              <Link to="/signup/business" className="flex-1" onClick={() => track("video_cta_click", { role: "business" })}>
                <Button data-testid="video-cta-business" className="w-full rounded-full bg-violet-600 hover:bg-fuchsia-600 text-white font-bold">I'm Hiring</Button>
              </Link>
            </div>
            <button type="button" onClick={start} data-testid="video-replay"
              className="inline-flex items-center gap-1.5 mt-4 text-sm text-slate-400 hover:text-white transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Replay
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
